import {ResolvingLitElement} from "../../lib/ResolvingLitElement.js";
import {html} from "lit";
import {TimesheetEntryRepository} from "../TimesheetEntryRepository.js";
import {NO_CUSTOMER_ID} from "../TimesheetEntry.js";
import {TimeTool} from "../../lib/TimeTool.js";
import {Chart} from "chart.js/auto";
import {HTML} from "../../lib/HTML.js";
import {DateTime} from "luxon";
import {mdiPauseCircle, mdiChevronRight, mdiCash, mdiTools, mdiCalendarWeekBegin, mdiAccountCash, mdiViewWeek} from "@mdi/js";
import {themeSignal} from "../../app/themeSignal.js";
import {effect} from "../../lib/effect.js";
import {TimesheetReportTool} from "../TimesheetReportTool.js";

export class TodayTimeSummaryWidget extends ResolvingLitElement {

    static properties = {
        today: {reflect: true, type: String},
        dailyWorkSeconds: {type: Number},
        billableSeconds: {type: Number},
        billableTemporarySeconds: {type: Number},
        notBillableSeconds: {type: Number},
        notBillableTemporarySeconds: {type: Number},
        plannedBillableSeconds: {type: Number},
        plannedNotBillableSeconds: {type: Number},
        weeklyLoggedSecondsSinceNow: {type: Number},
        weeklyPlannedSecondsSinceNow: {type: Number},
        monthlyLoggedSecondsSinceNow: {type: Number},
        monthlyPlannedSecondsSinceNow: {type: Number},
        runningTaskName: {type: String},
        runningStatus: {type: Boolean},
        todayTitle: {type: String},
        theme: {type: String}
    };

    /**
     * @private {ReactiveArray<TimesheetEntry>}
     */
    _entries;

    /**
     * @protected {ReactiveArray<PlannedTime>}
     */
    _plannedTimes;

    /**
     *
     * @protected {TimesheetReportTool}
     */
    _reportTool;

    constructor() {
        super();
        this.billableSeconds = 0;
        this.notBillableSeconds = 0;
        this.billableTemporarySeconds = 0;
        this.notBillableTemporarySeconds = 0;
        this.plannedBillableSeconds = 0;
        this.plannedNotBillableSeconds = 0;
        this.weeklyLoggedSecondsSinceNow = 0;
        this.weeklyPlannedSecondsSinceNow = 0;
        this.monthlyLoggedSecondsSinceNow = 0;
        this.monthlyPlannedSecondsSinceNow = 0;
        this.today = this._todayAsString();
        this.runningTaskName = '';
        this.runningStatus = false;
        this.dailyWorkSeconds = 60*60*8;
        this._billableHourColor = HTML.cssVariable('billable-color');
        this._unBillableHourColor = HTML.cssVariable('not-billable-color');
        this._reportTool = new TimesheetReportTool();
    }
    render() {
        return html`
            <div class="card today-widget">
                <header>
                    <h4>${this.todayTitle}</h4>
                </header>
                <div class="row">
                    <div class="col"><div style="width: 150px; height: 150px; margin-left: 50px;"><canvas id="today-donut"></canvas></div></div>
                    <div class="col today-hours">
                        <div id="today-total-hours">${TimeTool.formatDurationFromSeconds(this.totalSeconds, false)}<span class="of">/${TimeTool.formatDurationFromSeconds(this.dailyWorkSeconds, false)}</span></div>
                        <div id="today-billable-hours" class="sub-summary"><span class="today-billable-hours">${TimeTool.formatDurationFromSeconds(this.totalBillableSeconds, false)}</span><span class="of">/${TimeTool.formatDurationFromSeconds(this.plannedBillableSeconds, false)}</span> <svg class="in-text" style="width:24px;height:24px;" viewBox="0 0 30 30"><path d="${mdiAccountCash}" fill="#aaa" /></svg></div>
                        <div id="today-not-billable-hours" class="sub-summary"><span class="today-not-billable-hours">${TimeTool.formatDurationFromSeconds(this.totalNotBillableSeconds, false)}</span><span class="of">/${TimeTool.formatDurationFromSeconds(this.plannedNotBillableSeconds, false)} <svg class="in-text" style="width:24px;height:24px;" viewBox="0 0 30 30"><path d="${mdiTools}" fill="#aaa" /></svg></span></div>
                        <div id="week-billable-hours" class="sub-summary"><span class="today-billable-hours">${TimeTool.formatDurationFromSeconds(this.weeklyLoggedSecondsSinceNow+this.totalBillableSeconds, false)}</span><span class="of">/${TimeTool.formatDurationFromSeconds(this.weeklyPlannedSecondsSinceNow+this.plannedBillableSeconds, false)} <svg class="in-text" style="width:24px;height:24px" viewBox="0 0 30 30"><path d="${mdiViewWeek}" fill="#aaa" /></svg></span></div>
                        <div id="monthly-billable-hours" class="sub-summary"><span class="today-billable-hours">${TimeTool.formatDurationFromSeconds(this.monthlyLoggedSecondsSinceNow+this.totalBillableSeconds, false)}</span><span class="of">/${TimeTool.formatDurationFromSeconds(this.monthlyPlannedSecondsSinceNow+this.plannedBillableSeconds, false)} <svg class="in-text" style="width:24px;height:24px" viewBox="0 0 30 30"><path d="${mdiCalendarWeekBegin}" fill="#aaa" /></svg></span></div>
                    </div>
                </div>
                <footer>
                    ${this.runningStatus ? html`<svg style="width:30px;height:30px;display:inline-block;" viewBox="0 0 24 24"><path fill="#ff3c00" d="${mdiPauseCircle}" /></svg><span class="running-task-time">${TimeTool.formatDurationFromSeconds(this.totalTemporarySeconds, true)}</span> <span class="running-task-name">${this.runningTaskName}</span>` : ''}
                </footer>

            </div>`;
    }

    createRenderRoot() {
        return this;
    }

    connectedCallback() {
        super.connectedCallback();
        this._entries = this._get('timesheet-entries');
        this._plannedTimes = this._get('planned-times');

        effect(() => {
            this.theme = themeSignal.get();
        });
    }

    disconnectedCallback() {
        super.disconnectedCallback();

        this._entries.filled.delete(this._updateByRefill)
        this._entries.pushed.delete(this._updateByPushedOrUpdated);
        this._entries.itemUpdated.delete(this._updateByPushedOrUpdated);
        this._plannedTimes.filled.delete(this._updateByRefill)
        this._plannedTimes.pushed.delete(this._updateByPushedOrUpdated);
        this._plannedTimes.itemUpdated.delete(this._updateByPushedOrUpdated);

    }

    firstUpdated(_changedProperties) {
        super.firstUpdated(_changedProperties);
        this.chart = this._createChart();
        this._entries.filled.listen(this._updateByRefill);
        this._entries.pushed.listen(this._updateByPushedOrUpdated);
        this._entries.itemUpdated.listen(this._updateByPushedOrUpdated);
        this._plannedTimes.filled.listen(this._updateByRefill);
        this._plannedTimes.pushed.listen(this._updateByPushedOrUpdated);
        this._plannedTimes.itemUpdated.listen(this._updateByPushedOrUpdated);

    }

    get todayTitle() {
        let now = DateTime.now();
        if (this.today === now.toISODate()) {
            return 'Today';
        }
        if (this.today === now.minus({days:1}).toISODate()) {
            return 'Yesterday';
        }
        return DateTime.fromISO(this.today).setZone('Europe/Berlin').toLocaleString();
    }

    /**
     * @return {number}
     */
    get totalSeconds() {
        return this.totalBillableSeconds + this.totalNotBillableSeconds;
    }

    /**
     * @return {number}
     */
    get totalBillableSeconds() {
        return this.billableSeconds + this.billableTemporarySeconds
    }

    /**
     * @return {number}
     */
    get totalNotBillableSeconds() {
        return this.notBillableSeconds + this.notBillableTemporarySeconds;
    }

    /**
     * @return {number}
     */
    get totalTemporarySeconds() {
        return this.billableTemporarySeconds + this.notBillableTemporarySeconds;
    }

    updated(_changedProperties) {
        super.updated(_changedProperties);
        this._updateChart();
        if (_changedProperties.has('today') && this.today) {
            this._updateByRefill();
        }
        if (_changedProperties.has('theme') && this.theme) {
            this._updateChartColors();
        }
    }

    _updateByRefill = () => {

        // Is this needed?
        this.weeklyPlannedSecondsSinceNow = 0;

        let report = this._reportTool.createTodayReport(new Date(this.today), this._entries.items(), this._plannedTimes.items());
        this.dailyWorkSeconds = report.plannedBillable + report.plannedNotBillable;

        this.billableSeconds = report.billable;
        this.notBillableSeconds = report.notBillable;
        this.plannedBillableSeconds = report.plannedBillable;
        this.plannedNotBillableSeconds = report.plannedNotBillable;
        this.weeklyLoggedSecondsSinceNow = report.weeklyLoggedBillableSecondsBeforeToday;
        this.weeklyPlannedSecondsSinceNow = report.weeklyPlannedBillableSecondsBeforeToday;
        this.monthlyLoggedSecondsSinceNow = report.monthlyLoggedBillableSecondsBeforeToday;
        this.monthlyPlannedSecondsSinceNow = report.monthlyPlannedBillableSecondsBeforeToday;

        if (!report.runningEntry) {
            this.runningStatus = false;
            this.billableTemporarySeconds = 0;
            this.notBillableTemporarySeconds = 0;
            return;
        }
        this.runningStatus = true;
        this._updateByPushedOrUpdated(report.runningEntry);

    }

    /**
     * @param {TimesheetEntry} entry
     * @private
     */
    _updateByPushedOrUpdated = (entry) => {
        if (TimeTool.dayAsString(entry.start) !== this.today) {
            return;
        }
        if (entry.isLogged) {
            // We have to wait for the next refill here, just observing running
            return;
        }

        this.runningTaskName = (entry.task && entry.task.name) ? entry.task.name : '';
        this.runningStatus = true;

        if (entry.isBillable()) {
            this.billableTemporarySeconds = entry.duration;
            return;
        }
        this.notBillableTemporarySeconds = entry.duration;
    };

    _updateChart() {

        let billable = this.billableSeconds + this.billableTemporarySeconds;
        let notBillable = this.notBillableSeconds + this.notBillableTemporarySeconds;
        let rest = this.dailyWorkSeconds - billable - notBillable;
        if (rest < 0) {
            rest = 0;
        }
        this.chart.data.datasets.forEach(dataset => {
            dataset.data = [notBillable, billable, rest];
        });
        this.chart.update();

    }

    _createChart() {
        const borderColor = HTML.cssVariable('bg-color');
        const data = {
            labels: [
                'Not billable',
                'Billable',
                'Hours left'
            ],
            datasets: [{
                label: 'Hours',
                data: [],
                backgroundColor: [
                    this._unBillableHourColor,
                    this._billableHourColor,
                    'rgb(163,163,163)'
                ],
                borderColor: [
                    borderColor,
                    borderColor,
                    borderColor
                ],
                hoverOffset: 4
            }]
        };

        return new Chart(this.renderRoot.querySelector('#today-donut'), {
            type: 'doughnut',
            data: data,
            options: {
                responsive: true,
                plugins: {
                    legend: {
                        display: false
                    },
                    tooltip: {
                        callbacks: {
                            label: context => {
                                return TimeTool.formatDurationFromSeconds(context.raw);
                            }
                        }
                    }
                }
            }
        });
    }

    _updateChartColors() {

        if (!this.chart) {
            return;
        }
        const borderColor = HTML.cssVariable('bg-color');
        for (const key in this.chart.data.datasets) {
            this.chart.data.datasets[key].borderColor = [borderColor,borderColor,borderColor];
        }

    }

    /**
     * @return {string}
     * @private
     */
    _todayAsString() {
        return TimeTool.dayAsString();
    }
}

customElements.define('x-today-time-summary-widget', TodayTimeSummaryWidget);